import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { POPULAR_SPORTS } from "../../../constants/popularSports";
import L from "../../../constants/baysportLabels";
import s from "../clinic.module.css";
import { Button, Field, Input, useToast } from "../../ui";

function mergeById(base = [], extra = []) {
  const map = new Map();
  for (const row of base) {
    if (row?.id != null) map.set(String(row.id), row);
  }
  for (const row of extra) {
    if (row?.id != null) map.set(String(row.id), row);
  }
  return Array.from(map.values());
}

function normalizeCreated(res, fallbackName, keys = ["direction", "branch"]) {
  let raw = res;
  for (const key of keys) {
    if (res?.[key]) {
      raw = res[key];
      break;
    }
  }
  if (raw?.dataValues) raw = raw.dataValues;
  if (!raw || typeof raw !== "object") {
    return {
      id: `local_${Date.now()}`,
      name: fallbackName,
      is_active: true,
    };
  }
  return {
    ...raw,
    id: raw.id || `local_${Date.now()}`,
    name: raw.name || fallbackName,
    is_active: raw.is_active !== false,
  };
}

/**
 * Ordered gate: direction → hall → groups unlocked.
 * Keeps local progress so steps mark done immediately after add.
 */
export default function GroupsPrerequisiteSetup({
  directions: directionsProp = [],
  branches: branchesProp = [],
  onReady,
  onDirectionsChange,
  onBranchesChange,
}) {
  const toast = useToast();
  const [dirName, setDirName] = useState("");
  const [venueName, setVenueName] = useState("");
  const [saving, setSaving] = useState(false);
  const [localDirections, setLocalDirections] = useState([]);
  const [localBranches, setLocalBranches] = useState([]);

  useEffect(() => {
    setLocalDirections((prev) => mergeById(directionsProp, prev));
  }, [directionsProp]);

  useEffect(() => {
    setLocalBranches((prev) => mergeById(branchesProp, prev));
  }, [branchesProp]);

  const directions = useMemo(
    () => mergeById(directionsProp, localDirections),
    [directionsProp, localDirections],
  );
  const branches = useMemo(
    () => mergeById(branchesProp, localBranches),
    [branchesProp, localBranches],
  );

  const hasDir = directions.length > 0;
  const hasVenue = branches.length > 0;
  const gateStep = !hasDir ? 1 : !hasVenue ? 2 : 3;

  const addDirection = async (e) => {
    e.preventDefault();
    const name = dirName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const res = await clinicAPI.createSportDirection({ name });
      const row = normalizeCreated(res, name, ["direction"]);
      const next = mergeById(directions, [row]);
      setLocalDirections(next);
      onDirectionsChange?.(next);
      setDirName("");
      toast.success("Направление добавлено");
      await onReady?.();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Не удалось добавить направление",
      );
    } finally {
      setSaving(false);
    }
  };

  const addVenue = async (e) => {
    e.preventDefault();
    const name = venueName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const res = await clinicAPI.createBranch({
        name,
        is_active: true,
        is_default: branches.length === 0,
      });
      const row = normalizeCreated(res, name, ["branch"]);
      const next = mergeById(branches, [row]);
      setLocalBranches(next);
      onBranchesChange?.(next);
      setVenueName("");
      toast.success("Зал добавлен");
      await onReady?.();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Не удалось добавить зал",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={s.groupsHubEmpty}>
      <div className={s.groupsHubEmptyIcon}>🏃</div>
      <h2 className={s.groupsHubTitle}>{L.groups}</h2>
      <p className={s.groupsHubLead}>
        Группа связывает направление и зал. Сделайте шаги по порядку — без них
        группу создать нельзя.
      </p>

      <ol className={s.groupsSetupSteps}>
        <li
          className={`${s.groupsSetupStep} ${hasDir ? s.groupsSetupStepDone : ""} ${
            gateStep === 1 ? s.groupsSetupStepActive : ""
          }`}
        >
          <div className={s.groupsSetupStepHead}>
            <span className={s.groupsSetupBadge}>{hasDir ? "✓" : "1"}</span>
            <div>
              <strong>{L.direction}</strong>
              <p className={s.groupsSetupHint}>
                {hasDir
                  ? directions.map((d) => d.name).filter(Boolean).join(", ")
                  : "Вид спорта или активности секции"}
              </p>
            </div>
          </div>
          {gateStep === 1 ? (
            <form onSubmit={addDirection} className={s.groupsSetupForm}>
              <Field label="Название">
                <Input
                  list="groups-setup-sports"
                  value={dirName}
                  onChange={(e) => setDirName(e.target.value)}
                  placeholder="Дзюдо, ОФП, бокс…"
                  required
                  autoFocus
                />
                <datalist id="groups-setup-sports">
                  {POPULAR_SPORTS.map((sp) => (
                    <option key={sp} value={sp} />
                  ))}
                </datalist>
              </Field>
              <Button type="submit" disabled={saving || !dirName.trim()}>
                {saving ? "Сохраняем…" : "Добавить направление"}
              </Button>
              <Link to="/manage/app/directions" className={s.groupsSetupAltLink}>
                Открыть справочник направлений →
              </Link>
            </form>
          ) : null}
        </li>

        <li
          className={`${s.groupsSetupStep} ${hasVenue ? s.groupsSetupStepDone : ""} ${
            gateStep === 2 ? s.groupsSetupStepActive : ""
          } ${!hasDir ? s.groupsSetupStepLocked : ""}`}
        >
          <div className={s.groupsSetupStepHead}>
            <span className={s.groupsSetupBadge}>{hasVenue ? "✓" : "2"}</span>
            <div>
              <strong>{L.venue}</strong>
              <p className={s.groupsSetupHint}>
                {hasVenue
                  ? branches.map((b) => b.name).filter(Boolean).join(", ")
                  : hasDir
                    ? "Место, где проходят занятия"
                    : "Сначала добавьте направление"}
              </p>
            </div>
          </div>
          {gateStep === 2 ? (
            <form onSubmit={addVenue} className={s.groupsSetupForm}>
              <Field label="Название зала">
                <Input
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  placeholder="Основной зал"
                  required
                  autoFocus
                />
              </Field>
              <Button type="submit" disabled={saving || !venueName.trim()}>
                {saving ? "Сохраняем…" : "Добавить зал"}
              </Button>
              <Link to="/manage/app/venues" className={s.groupsSetupAltLink}>
                Открыть справочник залов →
              </Link>
            </form>
          ) : null}
        </li>

        <li
          className={`${s.groupsSetupStep} ${
            hasDir && hasVenue
              ? `${s.groupsSetupStepDone} ${s.groupsSetupStepActive}`
              : s.groupsSetupStepLocked
          }`}
        >
          <div className={s.groupsSetupStepHead}>
            <span className={s.groupsSetupBadge}>
              {hasDir && hasVenue ? "✓" : "3"}
            </span>
            <div>
              <strong>Создать группу</strong>
              <p className={s.groupsSetupHint}>
                {hasDir && hasVenue
                  ? "Справочники готовы — форма группы откроется сейчас"
                  : "Откроется, когда есть направление и зал"}
              </p>
            </div>
          </div>
        </li>
      </ol>
    </div>
  );
}
